import React from 'react';
import { Button, Typography, Progress, Statistic, Row, Col, Tag } from 'antd';
import { DownloadOutlined, CheckCircleOutlined, SaveOutlined, FireOutlined, AreaChartOutlined, LinkOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Results = ({ file }) => {
  // Check if file is a URL-based response (new format) or a File object (old format)
  const isUrlResponse = file.downloadUrl && !file.size;
  
  const handleDownload = () => {
    if (isUrlResponse) {
      // New URL-based download - add token to URL
      const token = localStorage.getItem('authToken');
      const urlWithToken = file.downloadUrl + (file.downloadUrl.includes('?') ? '&' : '?') + `token=${token}`;
      window.open(urlWithToken, '_blank');
    } else {
      // Old Blob-based download
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Use provided fileName or fallback to default
  const displayFileName = file.fileName || file.name || 'compressed_files.zip';
  
  // Use real compression stats from backend if available
  const fileSize = file.compressedSize || file.size || 1024 * 1024 * 2; // Default 2MB if unknown
  const originalSize = file.originalSize || fileSize * 1.4; // Use real original size or simulate 40% larger
  const compressionRatio = file.originalSize && file.compressedSize 
    ? Math.round(((file.originalSize - file.compressedSize) / file.originalSize) * 100)
    : 30; // Simulate 30% compression if not available
  const savedSpace = originalSize - fileSize;

  return (
    <div className="glass-panel-dark rounded-3xl overflow-hidden mt-4 animate-fade-in-up relative group">
      {/* Decorative Glow */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 opacity-60"></div>
      
      {/* Success Header */}
      <div className="px-6 py-4 border-b border-white/5 relative z-10 flex items-center gap-4 bg-gradient-to-r from-emerald-900/40 to-transparent">
        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.3)] animate-pulse-glow">
          <CheckCircleOutlined className="text-emerald-400 text-2xl" />
        </div>
        <div>
          <Title level={4} className="!mb-0.5 !text-slate-100 !font-bold tracking-tight text-glow flex items-center gap-3">
            Optimization Complete <span className="text-emerald-400 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 uppercase tracking-wider">Success</span>
          </Title>
          <Text className="text-slate-400 text-xs">
            Your payload has been successfully compressed
          </Text>
        </div>
      </div>

      <div className="p-6 space-y-6 relative z-10">
        {/* File Info */}
        <div className="bg-slate-900/50 border border-slate-700/50 rounded-2xl p-4 flex flex-col md:flex-row items-center gap-4 justify-between transform transition-transform duration-300 hover:scale-[1.01] hover:bg-slate-800/80">
          <div className="flex items-center gap-4 w-full">
          <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shadow-inner border border-slate-700/80">
            <SaveOutlined className="text-purple-400 text-2xl" />
          </div>
          <div className="flex-1 min-w-0">
            <Text strong className="text-slate-200 block text-lg truncate tracking-wide">
              {displayFileName}
            </Text>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Tag className="m-0 bg-slate-800 border-slate-600 text-slate-300 px-3 py-1 rounded-full border">
                ZIP ARCHIVE
              </Tag>
              {file.downloadUrl && (
                <Tag className="m-0 bg-purple-500/10 border-purple-500/30 text-purple-400 px-3 py-1 rounded-full border cursor-pointer hover:bg-purple-500/20" onClick={() => {
                  const token = localStorage.getItem('authToken');
                  const urlWithToken = file.downloadUrl + (file.downloadUrl.includes('?') ? '&' : '?') + `token=${token}`;
                  window.open(urlWithToken, '_blank');
                }}>
                  <LinkOutlined className="mr-1.5" /> Direct Download Link
                </Tag>
              )}
              {!isUrlResponse && (
                <Tag className="m-0 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 px-3 py-1 rounded-full border">
                  {formatFileSize(file.size)}
                </Tag>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Compression Stats */}
        <div className="space-y-6">
          <Title level={4} className="!mb-6 !text-slate-200 flex items-center gap-3 tracking-wide uppercase text-sm font-semibold">
            <AreaChartOutlined className="text-purple-400 text-lg mr-1.5" />
            Telemetry Data
          </Title>
          
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}>
              <div className="text-center p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:bg-slate-800/60 transition-colors shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-12 h-12 bg-slate-700/20 rounded-bl-full -z-10 transition-transform group-hover:scale-150"></div>
                <Statistic
                  title={<span className="text-[10px] text-slate-400 font-semibold tracking-wider uppercase">Raw Size</span>}
                  value={formatFileSize(originalSize)}
                  valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#94a3b8' }}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="text-center p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:bg-emerald-900/20 transition-colors shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-12 h-12 bg-emerald-500/10 rounded-bl-full -z-10 transition-transform group-hover:scale-150"></div>
                <Statistic
                  title={<span className="text-[10px] text-slate-400 font-semibold tracking-wider uppercase">Optimized</span>}
                  value={formatFileSize(fileSize)}
                  valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#34d399' }}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="text-center p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:bg-purple-900/20 transition-colors shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-12 h-12 bg-purple-500/10 rounded-bl-full -z-10 transition-transform group-hover:scale-150"></div>
                <Statistic
                  title={<span className="text-[10px] text-slate-400 font-semibold tracking-wider uppercase">Saved</span>}
                  value={formatFileSize(savedSpace)}
                  valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#a78bfa' }}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="text-center p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:bg-orange-900/20 transition-colors shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-12 h-12 bg-orange-500/10 rounded-bl-full -z-10 transition-transform group-hover:scale-150"></div>
                <Statistic
                  title={<span className="text-[10px] text-slate-400 font-semibold tracking-wider uppercase">Ratio</span>}
                  value={`${compressionRatio}%`}
                  valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#fb923c' }}
                />
              </div>
            </Col>
          </Row>

          {/* Compression Progress */}
          <div className="pt-4 bg-slate-800/20 p-4 rounded-xl border border-slate-700/30">
            <div className="flex items-center justify-between mb-3">
              <Text className="text-[10px] text-slate-300 font-semibold tracking-wide uppercase flex items-center gap-2">
                <FireOutlined className="text-orange-400 mr-1" /> Bandwidth Reduction
              </Text>
              <div className="bg-orange-500/10 border border-orange-500/30 px-2 py-0.5 rounded-full">
                <Text strong className="text-orange-400 text-[10px] font-mono">{compressionRatio}% LESS DATA</Text>
              </div>
            </div>
            <Progress
              percent={compressionRatio}
              status="success"
              strokeColor={{
                '0%': '#10b981',
                '100%': '#34d399',
              }}
              trailColor="rgba(30, 41, 59, 0.5)"
              showInfo={false}
              strokeWidth={10}
              className="mb-0 drop-shadow-[0_0_10px_rgba(16,185,129,0.3)]"
            />
          </div>
        </div>

        {/* Download Button */}
        <div className="pt-4">
          <Button
            type="primary"
            icon={<DownloadOutlined className="text-xl" />}
            size="large"
            onClick={handleDownload}
            block
            className="h-14 rounded-xl font-bold text-base uppercase tracking-wider shadow-[0_0_20px_rgba(16,185,129,0.2)] hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
            style={{ 
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              border: 'none'
            }}
          >
            Download Payload
            <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
          </Button>
        </div>

      </div>
    </div>
  );
};

export default Results;